import { create } from "zustand";
import { send_request } from "../../utils/Request";
import { TEAM_API_BASE } from "../../api/teamsApi";
import {
  extractApiStatusCode,
  resolveApiErrorMessage,
} from "./agentStoreUtils";

const AGENT_QUEUE_API_BASE = "ai-service/ai/agent";

function basePath(surface, chatSessionId) {
  return surface === "team"
    ? `${TEAM_API_BASE}/session/${chatSessionId}`
    : `${AGENT_QUEUE_API_BASE}/session/${chatSessionId}`;
}

function queueItems(value) {
  return Array.isArray(value) ? value : [];
}

function requireSuccess(response, fallback) {
  const status = extractApiStatusCode(response);
  if (status >= 400) {
    const error = new Error(resolveApiErrorMessage(response, fallback));
    error.status = status;
    throw error;
  }
  return response?.data;
}

function upsert(items, item) {
  if (!item?.queueId) return items;
  if (item.status !== "PENDING") {
    return items.filter((current) => current.queueId !== item.queueId);
  }
  const next = [...items];
  const index = next.findIndex((current) => current.queueId === item.queueId);
  if (index >= 0) {
    next[index] = { ...next[index], ...item };
  } else next.push(item);
  return next.sort((a, b) => {
    const time = String(a.createdAt || "").localeCompare(
      String(b.createdAt || "")
    );
    return time || String(a.queueId).localeCompare(String(b.queueId));
  });
}

const useQueuedMessageStore = create((set, get) => ({
  bySessionId: {},
  loadingBySessionId: {},
  errorBySessionId: {},
  versionBySessionId: {},
  revisionBySessionId: {},
  generationBySessionId: {},

  fetchQueue: async (chatSessionId, surface = "agent") => {
    if (!chatSessionId) return [];
    const startRevision = get().revisionBySessionId[chatSessionId] || 0;
    const generation = (get().generationBySessionId[chatSessionId] || 0) + 1;
    set((state) => ({
      generationBySessionId: {
        ...state.generationBySessionId,
        [chatSessionId]: generation,
      },
      loadingBySessionId: {
        ...state.loadingBySessionId,
        [chatSessionId]: true,
      },
    }));
    try {
      const response = await send_request(
        `${basePath(surface, chatSessionId)}/queued-messages`,
        "",
        {},
        "GET"
      );
      const items = queueItems(
        requireSuccess(response, "Failed to load queued messages")
      );
      set((state) => {
        if (state.generationBySessionId[chatSessionId] !== generation) {
          return {};
        }
        const versions = {
          ...(state.versionBySessionId[chatSessionId] || {}),
        };
        let reconciled =
          (state.revisionBySessionId[chatSessionId] || 0) === startRevision
            ? []
            : queueItems(state.bySessionId[chatSessionId]);
        for (const item of items) {
          if (!item?.queueId) continue;
          const version = Number(item.version || 0);
          if (version < (versions[item.queueId] || 0)) continue;
          versions[item.queueId] = version;
          reconciled = upsert(reconciled, item);
        }
        return {
          bySessionId: {
            ...state.bySessionId,
            [chatSessionId]: reconciled,
          },
          versionBySessionId: {
            ...state.versionBySessionId,
            [chatSessionId]: versions,
          },
          loadingBySessionId: {
            ...state.loadingBySessionId,
            [chatSessionId]: false,
          },
          errorBySessionId: {
            ...state.errorBySessionId,
            [chatSessionId]: null,
          },
        };
      });
      return items;
    } catch (error) {
      set((state) => {
        if (state.generationBySessionId[chatSessionId] !== generation) {
          return {};
        }
        return {
          loadingBySessionId: {
            ...state.loadingBySessionId,
            [chatSessionId]: false,
          },
          errorBySessionId: {
            ...state.errorBySessionId,
            [chatSessionId]: error?.message || "Failed to load queued messages",
          },
        };
      });
      return [];
    }
  },

  edit: async (chatSessionId, surface, queueId, message, expectedVersion) => {
    const response = await send_request(
      `${basePath(surface, chatSessionId)}/queued-messages/${queueId}`,
      { message, expectedVersion },
      {},
      "PUT"
    );
    const data = requireSuccess(response, "Failed to edit queued message");
    if (data) {
      get().applyQueueEvent(chatSessionId, "queued_message_updated", data);
    }
    return data;
  },

  cancel: async (chatSessionId, surface, queueId) => {
    const response = await send_request(
      `${basePath(surface, chatSessionId)}/queued-messages/${queueId}/cancel`,
      {},
      {},
      "POST"
    );
    const data = requireSuccess(response, "Failed to cancel queued message");
    get().applyQueueEvent(
      chatSessionId,
      "queued_message_cancelled",
      data || { queueId }
    );
    return data;
  },

  dispatchNext: async (chatSessionId, surface) => {
    const response = await send_request(
      `${basePath(surface, chatSessionId)}/queued-messages/dispatch-next`,
      {},
      {},
      "POST"
    );
    const data = requireSuccess(response, "Failed to send queued message");
    if (data?.queuedMessage) {
      get().applyQueueEvent(
        chatSessionId,
        "queued_message_dispatched",
        data.queuedMessage
      );
    }
    return data;
  },

  applyQueueEvent: (chatSessionId, eventType, queuedMessage) => {
    if (!chatSessionId || !queuedMessage?.queueId) return;
    set((state) => {
      const current = queueItems(state.bySessionId[chatSessionId]);
      const versions = {
        ...(state.versionBySessionId[chatSessionId] || {}),
      };
      const currentVersion = versions[queuedMessage.queueId] || 0;
      const incomingVersion = Number(queuedMessage.version ?? currentVersion);
      if (incomingVersion < currentVersion) return {};
      versions[queuedMessage.queueId] = incomingVersion;
      const next =
        eventType === "queued_message_cancelled" ||
        eventType === "queued_message_dispatched"
          ? current.filter((item) => item.queueId !== queuedMessage.queueId)
          : upsert(current, queuedMessage);
      return {
        bySessionId: { ...state.bySessionId, [chatSessionId]: next },
        versionBySessionId: {
          ...state.versionBySessionId,
          [chatSessionId]: versions,
        },
        revisionBySessionId: {
          ...state.revisionBySessionId,
          [chatSessionId]: (state.revisionBySessionId[chatSessionId] || 0) + 1,
        },
      };
    });
  },

  clearSession: (chatSessionId) => {
    set((state) => {
      const bySessionId = { ...state.bySessionId };
      const loadingBySessionId = { ...state.loadingBySessionId };
      const errorBySessionId = { ...state.errorBySessionId };
      const versionBySessionId = { ...state.versionBySessionId };
      const revisionBySessionId = { ...state.revisionBySessionId };
      const generationBySessionId = {
        ...state.generationBySessionId,
        [chatSessionId]: (state.generationBySessionId[chatSessionId] || 0) + 1,
      };
      delete bySessionId[chatSessionId];
      delete loadingBySessionId[chatSessionId];
      delete errorBySessionId[chatSessionId];
      delete versionBySessionId[chatSessionId];
      delete revisionBySessionId[chatSessionId];
      return {
        bySessionId,
        loadingBySessionId,
        errorBySessionId,
        versionBySessionId,
        revisionBySessionId,
        generationBySessionId,
      };
    });
  },
}));

export default useQueuedMessageStore;
